import { FC, useEffect, useState } from 'react';
import { DatePicker, Form, Input, Modal } from 'antd';
import { Dayjs } from 'dayjs';
import { markInvoicePaid } from 'services/api/invoicing';
import { Invoice } from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  invoice: Invoice | null; // modal is open while non-null
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  paidAt?: Dayjs | null;
  receiptUrl?: string;
};

const MarkPaidModal: FC<IProps> = ({ invoice, onClose, onSuccess }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invoice) {
      form.resetFields();
    }
  }, [invoice, form]);

  const onFinish = async (values: FormValues) => {
    if (!invoice) {
      return;
    }

    try {
      setLoading(true);
      await markInvoicePaid(invoice.publicId, {
        paidAt: values.paidAt ? values.paidAt.toISOString() : undefined,
        receiptUrl: values.receiptUrl?.trim() || undefined
      });
      onSuccess();
      onClose();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={!!invoice}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Mark paid"
      title={`Mark invoice ${invoice?.invoiceNumber ?? ''} as paid`}
      confirmLoading={loading}
      closable={!loading}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Paid at"
          name="paidAt"
          help="Defaults to now if left empty"
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Receipt URL" name="receiptUrl">
          <Input placeholder="Link to the bank receipt (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default MarkPaidModal;
